import { http, HttpResponse } from 'msw';
import { mockAppAccounts, findAppAccountById, AppAccount } from '../data/appAccounts';
import { mockOrganizations, findOrganizationById, Organization } from '../data/organizations';
import { mockOrders, findOrderById } from '../data/orders';
import { PaginationResponse } from '../../types/api';

// Helper function to create error responses
const createErrorResponse = (message: string, code: number, status: number) => {
  return HttpResponse.json({
    success: false,
    data: null,
    message,
    code
  }, { status });
};

// Helper function to extract user ID from token
const getUserIdFromToken = (authHeader: string | null): string | null => {
  if (!authHeader || !authHeader.startsWith('Bearer ')) {
    return null;
  }
  
  const token = authHeader.substring(7);
  const tokenMatch = token.match(/mock-jwt-token-(\d+)-/);
  return tokenMatch ? tokenMatch[1] : null;
};

// Helper function to check if user is super admin
const isSuperAdmin = (userId: string): boolean => {
  return userId === '1'; // User ID 1 is super admin in our mock data
};

// Helper function to paginate results
const paginateResults = <T>(
  items: T[],
  page: number = 1,
  pageSize: number = 10
): { items: T[]; pagination: PaginationResponse } => {
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedItems = items.slice(startIndex, endIndex);
  
  return {
    items: paginatedItems,
    pagination: {
      page,
      pageSize,
      total: items.length,
      totalPages: Math.ceil(items.length / pageSize)
    }
  };
};

export const adminHandlers = [
  // Get app accounts (Admin: App账号管理)
  http.get('/api/v1/admin/app-accounts', (async ({ request }: { request: Request }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const userId = getUserIdFromToken(authHeader);
      
      if (!userId || !isSuperAdmin(userId)) {
        return createErrorResponse('Unauthorized - Super admin access required', 403, 403);
      }
      
      const url = new URL(request.url);
      const page = parseInt(url.searchParams.get('page') || '1');
      const pageSize = parseInt(url.searchParams.get('pageSize') || '10');
      const search = url.searchParams.get('search') || '';
      const status = url.searchParams.get('status') || '';
      
      // Filter app accounts based on search and status
      let filteredAccounts = mockAppAccounts;
      
      if (search) {
        filteredAccounts = filteredAccounts.filter(account => 
          account.name.toLowerCase().includes(search.toLowerCase()) ||
          account.email.toLowerCase().includes(search.toLowerCase()) ||
          account.organizationName?.toLowerCase().includes(search.toLowerCase())
        );
      }
      
      if (status) {
        filteredAccounts = filteredAccounts.filter(account => account.status === status);
      }
      
      const result = paginateResults(filteredAccounts, page, pageSize);
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 600));
      
      return HttpResponse.json({
        success: true,
        data: {
          accounts: result.items,
          pagination: result.pagination
        },
        message: 'App accounts retrieved successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Failed to retrieve app accounts', 500, 500);
    }
  }) as any),

  // Get app account by ID
  http.get('/api/v1/admin/app-accounts/:accountId', async ({ request, params }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const userId = getUserIdFromToken(authHeader);
      
      if (!userId || !isSuperAdmin(userId)) {
        return createErrorResponse('Unauthorized - Super admin access required', 403, 403);
      }
      
      const { accountId } = params;
      const account = findAppAccountById(accountId as string);
      
      if (!account) {
        return createErrorResponse('App account not found', 404, 404);
      }
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 400));
      
      return HttpResponse.json({
        success: true,
        data: account,
        message: 'App account details retrieved successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Failed to retrieve app account details', 500, 500);
    }
  }),

  // Get organizations (Admin: 机构管理)
  http.get('/api/v1/admin/organizations', async ({ request }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const userId = getUserIdFromToken(authHeader);
      
      if (!userId || !isSuperAdmin(userId)) {
        return createErrorResponse('Unauthorized - Super admin access required', 403, 403);
      }
      
      const url = new URL(request.url);
      const page = parseInt(url.searchParams.get('page') || '1');
      const pageSize = parseInt(url.searchParams.get('pageSize') || '10');
      const search = url.searchParams.get('search') || '';
      const status = url.searchParams.get('status') || '';
      
      // Filter organizations based on search and status
      let filteredOrgs = mockOrganizations;
      
      if (search) {
        filteredOrgs = filteredOrgs.filter(org => 
          org.name.toLowerCase().includes(search.toLowerCase()) ||
          org.description?.toLowerCase().includes(search.toLowerCase())
        );
      }
      
      if (status) {
        filteredOrgs = filteredOrgs.filter(org => org.status === status);
      }
      
      const result = paginateResults(filteredOrgs, page, pageSize);
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return HttpResponse.json({
        success: true,
        data: {
          organizations: result.items,
          pagination: result.pagination
        },
        message: 'Organizations retrieved successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Failed to retrieve organizations', 500, 500);
    }
  }),

  // Get orders (Admin: 订单管理)
  http.get('/api/v1/admin/orders', async ({ request }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const userId = getUserIdFromToken(authHeader);
      
      if (!userId || !isSuperAdmin(userId)) {
        return createErrorResponse('Unauthorized - Super admin access required', 403, 403);
      }
      
      const url = new URL(request.url);
      const page = parseInt(url.searchParams.get('page') || '1');
      const pageSize = parseInt(url.searchParams.get('pageSize') || '10');
      const search = url.searchParams.get('search') || '';
      const status = url.searchParams.get('status') || '';
      const planType = url.searchParams.get('planType') || '';
      
      // Filter orders based on search, status, and plan type
      let filteredOrders = mockOrders;
      
      if (search) {
        filteredOrders = filteredOrders.filter(order => 
          order.organizationName.toLowerCase().includes(search.toLowerCase()) ||
          order.id.toLowerCase().includes(search.toLowerCase())
        );
      }
      
      if (status) {
        filteredOrders = filteredOrders.filter(order => order.status === status);
      }
      
      if (planType) {
        filteredOrders = filteredOrders.filter(order => order.planType === planType);
      }
      
      const result = paginateResults(filteredOrders, page, pageSize);
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 550));
      
      return HttpResponse.json({
        success: true,
        data: {
          orders: result.items,
          pagination: result.pagination
        },
        message: 'Orders retrieved successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Failed to retrieve orders', 500, 500);
    }
  }),

  // Get order by ID
  http.get('/api/v1/admin/orders/:orderId', async ({ request, params }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const userId = getUserIdFromToken(authHeader);
      
      if (!userId || !isSuperAdmin(userId)) {
        return createErrorResponse('Unauthorized - Super admin access required', 403, 403);
      }
      
      const { orderId } = params;
      const order = findOrderById(orderId as string);
      
      if (!order) {
        return createErrorResponse('Order not found', 404, 404);
      }
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 400));
      
      return HttpResponse.json({
        success: true,
        data: order,
        message: 'Order details retrieved successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Failed to retrieve order details', 500, 500);
    }
  }),

  // Update app account status
  http.put('/api/v1/admin/app-accounts/:accountId/status', async ({ request, params }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const userId = getUserIdFromToken(authHeader);
      
      if (!userId || !isSuperAdmin(userId)) {
        return createErrorResponse('Unauthorized - Super admin access required', 403, 403);
      }
      
      const { accountId } = params;
      const account = findAppAccountById(accountId as string);
      
      if (!account) {
        return createErrorResponse('App account not found', 404, 404);
      }
      
      const { status } = await request.json() as { status: string };
      
      const updatedAccount: AppAccount = {
        ...account,
        status: status as AppAccount['status'],
        updatedAt: new Date().toISOString()
      };
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 700));
      
      return HttpResponse.json({
        success: true,
        data: updatedAccount,
        message: 'App account status updated successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Failed to update app account status', 500, 500);
    }
  }),

  // Update organization status
  http.put('/api/v1/admin/organizations/:organizationId/status', async ({ request, params }) => {
    try {
      const authHeader = request.headers.get('Authorization');
      const userId = getUserIdFromToken(authHeader);
      
      if (!userId || !isSuperAdmin(userId)) {
        return createErrorResponse('Unauthorized - Super admin access required', 403, 403);
      }
      
      const { organizationId } = params;
      const organization = findOrganizationById(organizationId as string);
      
      if (!organization) {
        return createErrorResponse('Organization not found', 404, 404);
      }
      
      const { status } = await request.json() as { status: string };
      
      const updatedOrganization: Organization = {
        ...organization,
        status: status as Organization['status'],
        updatedAt: new Date().toISOString()
      };
      
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 650));
      
      return HttpResponse.json({
        success: true,
        data: updatedOrganization,
        message: 'Organization status updated successfully'
      });
      
    } catch (error) {
      return createErrorResponse('Failed to update organization status', 500, 500);
    }
  })
];